// ═══════════════════════════════════════════════════════════════
// TODO LIST VIEW - Helper Functions
// ═══════════════════════════════════════════════════════════════

import { sortTasks } from '../../components/SortDropdown';
import { parseMissionDate } from '../../../../utils/missionDates';

/**
 * Recursively collect all nestedSprintIds from tasks
 * Returns a Set of all sprint IDs that need to be loaded
 */
export const collectNestedSprintIds = (tasks, allSprints, collected = new Set(), visited = new Set()) => {
  tasks.forEach(task => {
    if (task.nestedSprintId && !visited.has(task.nestedSprintId)) {
      collected.add(task.nestedSprintId);
      visited.add(task.nestedSprintId);
      
      const nestedSprint = allSprints.find(s => s.id === task.nestedSprintId);
      if (nestedSprint?.tasks) {
        collectNestedSprintIds(nestedSprint.tasks, allSprints, collected, visited);
      }
    }
  });
  return collected;
};

/**
 * Build a nested tree structure for Master View (proper sticky behavior)
 * Each parent contains its children in a nested structure
 * Also tracks sprintTasks and sprintId for each node so actions route to correct sprint
 */
export const buildNestedTree = (tasks, allSprints, depth = 0, visitedSprintIds = new Set(), currentSprintTasks = null, currentSprintId = null) => {
  const nodes = [];
  // Use currentSprintTasks if provided, otherwise use tasks (for top-level)
  const sprintTasks = currentSprintTasks || tasks;
  // Track which sprint these tasks belong to
  const sprintId = currentSprintId;
  
  tasks.forEach(task => {
    let nestedSprint = null;
    if (task.nestedSprintId) {
      nestedSprint = allSprints.find(s => s.id === task.nestedSprintId);
    }
    
    const hasNestedContent = nestedSprint && nestedSprint.tasks && nestedSprint.tasks.length > 0;
    let children = [];
    
    // If this links to a nested sprint, recursively add its tasks as children
    if (hasNestedContent && !visitedSprintIds.has(nestedSprint.id)) {
      const newVisited = new Set(visitedSprintIds);
      newVisited.add(nestedSprint.id);
      
      const nestedChildren = buildNestedTree(
        nestedSprint.tasks,
        allSprints,
        depth + 1,
        newVisited,
        nestedSprint.tasks, // Pass the nested sprint's tasks
        nestedSprint.id // Pass the nested sprint's ID
      );
      children.push(...nestedChildren);
    }
    
    nodes.push({
      task,
      depth,
      isSubtask: false,
      nestedSprint,
      isParent: hasNestedContent,
      children,
      sprintTasks, // Tasks from the same sprint for dependency dropdown
      sprintId, // ID of the sprint this task belongs to (null = current/root sprint)
    });
  });
  
  return nodes;
};

/**
 * Build a flat list for Simple View (current campaign only)
 * Shows nested campaign indicators but doesn't drill into them
 */
export const buildSimpleList = (tasks, allSprints) => {
  const items = [];
  
  tasks.forEach(task => {
    let nestedSprint = null;
    if (task.nestedSprintId) {
      nestedSprint = allSprints.find(s => s.id === task.nestedSprintId);
    }
    
    // Add the task itself (depth 0, never a parent since we don't drill in)
    items.push({
      task,
      depth: 0,
      isSubtask: false,
      nestedSprint,
      isParent: false, // In simple view, don't expand nested campaigns
      hasNestedCampaign: !!nestedSprint, // But show indicator
      sprintTasks: tasks, // All tasks from same campaign for dependency dropdown
    });
  });
  
  return items;
};

/**
 * Build swimlane groups for Swimlane View
 */
export const buildSwimlaneGroups = (tasks, allSprints, statuses, allTags, groupByOption) => {
  const groups = {};
  
  // Initialize groups based on groupBy option
  if (groupByOption === 'status') {
    statuses.forEach(status => {
      groups[status.id] = {
        id: status.id,
        label: status.name,
        color: status.color,
        items: [],
        order: statuses.indexOf(status),
      };
    });
  } else if (groupByOption === 'priority') {
    const priorities = [
      { id: 'high', label: 'High Priority', color: '#ef4444', order: 0 },
      { id: 'medium', label: 'Medium Priority', color: '#f59e0b', order: 1 },
      { id: 'low', label: 'Low Priority', color: '#22c55e', order: 2 },
      { id: 'none', label: 'No Priority', color: '#6b7280', order: 3 },
    ];
    priorities.forEach(p => {
      groups[p.id] = { ...p, items: [] };
    });
  } else if (groupByOption === 'tag') {
    allTags.forEach((tag, index) => {
      groups[tag.id] = {
        id: tag.id,
        label: tag.name,
        color: tag.color || '#6b7280',
        items: [],
        order: index,
      };
    });
    groups['untagged'] = {
      id: 'untagged',
      label: 'Untagged',
      color: '#6b7280',
      items: [],
      order: allTags.length,
    };
  }
  
  // Assign tasks to groups
  tasks.forEach(task => {
    let nestedSprint = null;
    if (task.nestedSprintId) {
      nestedSprint = allSprints.find(s => s.id === task.nestedSprintId);
    }
    
    const item = {
      task,
      depth: 0,
      isSubtask: false,
      nestedSprint,
      isParent: false,
      hasNestedCampaign: !!nestedSprint,
      sprintTasks: tasks, // All tasks from same campaign for dependency dropdown
    };
    
    let groupKey;
    if (groupByOption === 'status') {
      groupKey = task.status || statuses[0]?.id;
    } else if (groupByOption === 'priority') {
      groupKey = task.priority || 'none';
    } else if (groupByOption === 'tag') {
      const primaryTag = task.tags?.[0];
      groupKey = primaryTag || 'untagged';
    }
    
    if (groups[groupKey]) {
      groups[groupKey].items.push(item);
    } else if (groupByOption === 'status' && statuses.length > 0) {
      // Fallback for unknown status
      groups[statuses[0].id]?.items.push(item);
    }
  });
  
  // Convert to sorted array
  return Object.values(groups)
    .filter(g => g.items.length > 0 || groupByOption === 'status') // Always show status lanes
    .sort((a, b) => a.order - b.order);
};

/**
 * Sort items based on sort option
 * Uses the shared sortTasks function from SortDropdown for consistency
 */
export const sortItems = (items, sortBy, statuses, customFields = []) => {
  if (sortBy === 'manual' || !items || items.length === 0) return items;
  
  // Extract tasks, sort them, then rebuild items in sorted order
  const tasks = items.map(item => item.task);
  const sortedTasks = sortTasks(tasks, sortBy, customFields);
  
  // Create a map of task id to sorted index
  const sortedOrder = new Map();
  sortedTasks.forEach((task, index) => {
    sortedOrder.set(task.id, index);
  });
  
  // Sort items based on their task's position in sortedTasks
  return [...items].sort((a, b) => {
    const indexA = sortedOrder.get(a.task.id) ?? 999;
    const indexB = sortedOrder.get(b.task.id) ?? 999;
    return indexA - indexB;
  });
};

/**
 * Sort tree nodes at each level (for Master view)
 * Recursively sorts children while preserving the tree structure
 * Uses the shared sortTasks function from SortDropdown for consistency
 * 
 * Sorting happens WITHIN each nesting level - tasks in a nested campaign
 * are sorted among themselves, not mixed with tasks from parent campaigns.
 */
export const sortTree = (nodes, sortBy, statuses, customFields = []) => {
  if (sortBy === 'manual' || !nodes || nodes.length === 0) return nodes;
  
  // Extract tasks from nodes at this level
  const tasks = nodes.map(node => node.task);
  const sortedTasks = sortTasks(tasks, sortBy, customFields);
  
  // Create a map of task id to sorted index
  const sortedOrder = new Map();
  sortedTasks.forEach((task, index) => {
    sortedOrder.set(task.id, index);
  });
  
  // Sort nodes at this level based on their task's position
  const sortedNodes = [...nodes].sort((a, b) => {
    const indexA = sortedOrder.get(a.task.id) ?? 999;
    const indexB = sortedOrder.get(b.task.id) ?? 999;
    return indexA - indexB;
  });
  
  // Recursively sort children of each node (nested campaigns)
  // This ensures sorting happens within each nesting level independently
  return sortedNodes.map(node => ({
    ...node,
    children: node.children && node.children.length > 0 
      ? sortTree(node.children, sortBy, statuses, customFields) 
      : node.children,
  }));
};

/**
 * Filter items based on filter option
 * For Master view, includes parent items if any child matches
 */
export const filterItems = (items, filterBy, statuses, isTree = false) => {
  if (filterBy === 'all') return items;
  
  const doneStatus = statuses.find(s => s.category === 'completed');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const matchesFilter = (item) => {
    const task = item.task;
    const isComplete = item.isSubtask 
      ? task.completed === true
      : task.status === doneStatus?.id || task.status === 'completed';
    
    switch (filterBy) {
      case 'incomplete':
        return !isComplete;
      case 'complete':
        return isComplete;
      case 'high-priority':
        return task.priority === 'high';
      case 'has-due-date':
        return !!task.dueDate;
      case 'overdue': {
        if (!task.dueDate) return false;
        const due = parseMissionDate(task.dueDate);
        if (!due) return false;
        return due.getTime() < Date.now() && !isComplete;
      }
      default:
        return true;
    }
  };
  
  if (!isTree) {
    // Simple filter for flat lists
    return items.filter(matchesFilter);
  }
  
  // For tree structures (Master view), include parents if any child matches
  const filterTree = (nodes) => {
    return nodes.reduce((acc, node) => {
      const filteredChildren = node.children ? filterTree(node.children) : [];
      const nodeMatches = matchesFilter(node);
      const hasMatchingChildren = filteredChildren.length > 0;
      
      if (nodeMatches || hasMatchingChildren) {
        acc.push({
          ...node,
          children: filteredChildren,
        });
      }
      
      return acc;
    }, []);
  };
  
  return filterTree(items);
};
